import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { FeatureFlagService } from '@galaxy/partner';
import { catchError, map, of, switchMap, take } from 'rxjs';
import { MARKET_ID_TOKEN, PARTNER_ID_TOKEN } from '../tokens';
import { CONFIGURATION_V2_FLAGS, configurationV2Enabled } from '../utils/configuration-v2-flag';

/**
 * Gates the assistant summary page on the `assistant_configuration_v2` flags.
 *
 * Resolves `false` rather than redirecting: these routes are mounted at four different paths
 * (business/partner/vendasta center and the AI knowledge shell), so there is no single sensible
 * fallback target. Reads the flags straight from FeatureFlagService rather than through
 * `AiAssistantService`, whose flag signal is seeded with `{}` and so answers `false` until the
 * response lands — a guard reading it would reject the first navigation.
 */
export const assistantConfigurationV2Guard: CanActivateFn = () => {
  const featureFlagService = inject(FeatureFlagService);
  const partnerId$ = inject(PARTNER_ID_TOKEN);
  const marketId$ = inject(MARKET_ID_TOKEN);

  return partnerId$.pipe(
    take(1),
    switchMap((partnerId) => {
      if (!partnerId) {
        return of(false);
      }
      return marketId$.pipe(
        take(1),
        switchMap((marketId) =>
          featureFlagService
            .batchGetStatus(partnerId, marketId ?? '', CONFIGURATION_V2_FLAGS)
            .pipe(take(1), map(configurationV2Enabled)),
        ),
      );
    }),
    catchError(() => of(false)),
  );
};
